import {create} from 'zustand';
import {translationsApi} from '@/api/translations';
import {getApiErrorMessage} from '@/api/client';

type TranslatedText = {
  text: string;
};

export type TranslationStore = {
  // Keyed by SOURCE text, not by item - the API's own cache dedupes the same way, and a channel's
  // boilerplate description repeated across many items only needs to be looked up here once too.
  byText: Map<string, TranslatedText>;
  // Keyed by an analyst-facing item key (e.g. the item id, or a synthetic key for a client-built
  // item that has none) - whether that card is currently showing the translation.
  shownFor: Set<string>;
  pendingFor: Set<string>;
  // Translation errors surface as inline text next to the toggle rather than as a UiStore toast,
  // so each card shows its own failure - keyed the same way.
  errorFor: Map<string, string>;
  translate: (itemKey: string, texts: string[]) => Promise<void>;
  translateMany: (entries: Array<{itemKey: string; texts: string[]}>) => Promise<void>;
  hide: (itemKey: string) => void;
};

export const useTranslationStore = create<TranslationStore>((set, get) => {
  async function fetchAndStore(texts: string[]): Promise<void> {
    const unseen = [...new Set(texts)].filter(
      (text) => text.trim() !== '' && !get().byText.has(text)
    );
    if (unseen.length === 0) {
      return;
    }

    const {translations} = await translationsApi.translate(unseen);
    set((state) => {
      const byText = new Map(state.byText);
      for (const [index, text] of unseen.entries()) {
        const segment = translations[index];
        if (segment) {
          // Coerced at this one boundary rather than threading `| null` through every consumer:
          // translated_text is only ever null for a null request segment, and every caller here
          // (TranslateToggle, getTranslatableSegments) already filters blanks before requesting -
          // this can't actually happen from any current caller, so falling back to the source
          // text keeps the store's internal invariant (a shown translation is always a string)
          // true and simple.
          byText.set(text, {text: segment.translated_text ?? text});
        }
      }

      return {byText};
    });
  }

  return {
    byText: new Map(),
    shownFor: new Set(),
    pendingFor: new Set(),
    errorFor: new Map(),

    async translate(itemKey, texts) {
      set((state) => ({
        pendingFor: new Set(state.pendingFor).add(itemKey),
        errorFor: withoutKey(state.errorFor, itemKey)
      }));

      try {
        await fetchAndStore(texts);
        set((state) => ({shownFor: new Set(state.shownFor).add(itemKey)}));
      } catch (error) {
        set((state) => ({
          errorFor: new Map(state.errorFor).set(
            itemKey,
            getApiErrorMessage(error, 'Translation failed')
          )
        }));
      } finally {
        set((state) => {
          const pendingFor = new Set(state.pendingFor);
          pendingFor.delete(itemKey);
          return {pendingFor};
        });
      }
    },

    async translateMany(entries) {
      const keys = entries.map((e) => e.itemKey);
      set((state) => ({
        pendingFor: new Set([...state.pendingFor, ...keys])
      }));

      try {
        await fetchAndStore(entries.flatMap((e) => e.texts));
        set((state) => ({shownFor: new Set([...state.shownFor, ...keys])}));
      } catch (error) {
        const message = getApiErrorMessage(error, 'Translation failed');
        set((state) => ({
          errorFor: new Map([...state.errorFor, ...keys.map((key) => [key, message] as const)])
        }));
      } finally {
        set((state) => {
          const pendingFor = new Set(state.pendingFor);
          for (const key of keys) {
            pendingFor.delete(key);
          }

          return {pendingFor};
        });
      }
    },

    hide(itemKey) {
      set((state) => {
        const shownFor = new Set(state.shownFor);
        shownFor.delete(itemKey);
        return {shownFor, errorFor: withoutKey(state.errorFor, itemKey)};
      });
    }
  };
});

function withoutKey<V>(map: Map<string, V>, key: string): Map<string, V> {
  if (!map.has(key)) {
    return map;
  }

  const next = new Map(map);
  next.delete(key);
  return next;
}

/**
 Renders `sourceText` translated once the given item key's translation has been shown, otherwise
 the original - the single hook every host site uses (for title, description, or tags) so one
 itemKey/toggle consistently swaps all of an item's translatable text together.
 */
export function useTranslatedText(
  itemKey: string,
  sourceText: string | null | undefined
): string | null | undefined {
  return useTranslationStore((state) =>
    sourceText && state.shownFor.has(itemKey)
      ? (state.byText.get(sourceText)?.text ?? sourceText)
      : sourceText
  );
}

/**
 Same as useTranslatedText, for a variable-length list of independent segments (e.g. one item's
 tags, each translated as its own segment) - a single store subscription rather than one
 useTranslatedText call per tag, which the variable count would make an invalid hook call anyway.
 */
export function useTranslatedTexts(itemKey: string, sourceTexts: string[]): string[] {
  // Two primitive/stable-reference subscriptions, not one selector that returns a fresh .map()
  // array - zustand v5 hands the selector straight to useSyncExternalStore with no memoisation, so
  // a selector returning a new array every call (even `[].map()`) never produces a stable snapshot
  // and React loops ("maximum update depth exceeded") on the very first render once shownFor has
  // the key. isShown is a boolean and byText is the store's own Map reference (stable unless
  // translate()/hide() actually replaces it) - both are safe useSyncExternalStore snapshots. The
  // .map() below runs as plain render logic, not inside the snapshot getter.
  const isShown = useTranslationStore((state) => state.shownFor.has(itemKey));
  const byText = useTranslationStore((state) => state.byText);
  return isShown ? sourceTexts.map((text) => byText.get(text)?.text ?? text) : sourceTexts;
}

/** Reset all translation state. Test-only - keeps unit tests independent of order. */
export function __resetTranslationStore(): void {
  useTranslationStore.setState({
    byText: new Map(),
    shownFor: new Set(),
    pendingFor: new Set(),
    errorFor: new Map()
  });
}
